import React, { useEffect, useRef, useState } from 'react';
import { StyledButton } from '../../../../../../globalStyled';
import {
  StyledPopUp,
  StyledPopUpBlock,
  StyledPopUpContainer,
  StyledButtonContainer,
  StyledHeader,
  StyledParagraph,
  StyledLoader,
} from '../../../../../../components/Popup/styled';
import { formatTransferFee } from '../../../../../../service/seasonTransfers';
import { OfferSummary } from './styled';

export default function BatchOfferPopup({
  action,
  offers,
  players,
  busy,
  progress,
  results,
  canConfirm,
  onConfirm,
  onClose,
}) {
  const [expanded, setExpanded] = useState(false);
  const dialog = useRef(null);
  const accepting = action === 'accept';
  const completed = results.length > 0;
  const entries = completed ? results : offers;
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);
  const onKeyDown = (event) => {
    if (event.key === 'Escape' && !busy) onClose();
    if (event.key === 'Tab') {
      const buttons = Array.from(
        dialog.current.querySelectorAll('button:not(:disabled)')
      );
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (!buttons.length) {
        event.preventDefault();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === dialog.current)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };
  return (
    <StyledPopUp onClick={busy ? undefined : onClose}>
      <StyledPopUpBlock
        role="dialog"
        aria-modal="true"
        aria-labelledby="batch-offer-title"
        tabIndex={-1}
        ref={dialog}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
      >
        <StyledPopUpContainer>
          <StyledHeader id="batch-offer-title">
            {completed
              ? 'Offers updated'
              : accepting
              ? 'Accept offers'
              : 'Decline offers'}
          </StyledHeader>
          {busy ? (
            <>
              <StyledLoader
                type="Bars"
                color="var(--tol-header-accent)"
                height={60}
                width={60}
              />
              <StyledParagraph role="status">{progress} offers processed</StyledParagraph>
            </>
          ) : (
            <>
              <StyledParagraph>
                {completed
                  ? `${results.filter((result) => result.success).length} of ${
                      results.length
                    } offers completed.`
                  : `Are you sure you want to ${accepting ? 'accept' : 'decline'} these ${
                      offers.length
                    } offers?`}
              </StyledParagraph>
              <OfferSummary>
                {(expanded ? entries : entries.slice(0, 4)).map((entry) => {
                  if (completed)
                    return (
                      <li key={entry.id}>
                        <span>{entry.name}</span>
                        <small>{entry.message}</small>
                      </li>
                    );
                  const player = players.find((p) => p._id === entry.player._id);
                  const outsideRange =
                    Number(entry.transfer_fee_offer) < player?.potential_min ||
                    Number(entry.transfer_fee_offer) > player?.potential_max;
                  return (
                    <li key={entry._id}>
                      <span>{entry.player.name}</span>
                      <strong>{formatTransferFee(entry.transfer_fee_offer)}</strong>
                      {entry.category === 1 && (
                        <small>
                          Loan until {new Date(entry.loan_end_date).toLocaleDateString()}
                        </small>
                      )}
                      {accepting && outsideRange && (
                        <small>Outside the recommended value range</small>
                      )}
                    </li>
                  );
                })}
                {entries.length > 4 && (
                  <li>
                    <button onClick={() => setExpanded(!expanded)}>
                      {expanded ? 'Show less' : `+${entries.length - 4} more`}
                    </button>
                  </li>
                )}
              </OfferSummary>
              <StyledButtonContainer>
                {completed ? (
                  <StyledButton primary onClick={onClose}>
                    Got it!
                  </StyledButton>
                ) : (
                  <>
                    <StyledButton basic onClick={onClose}>
                      Cancel
                    </StyledButton>
                    <StyledButton
                      primary={accepting}
                      danger={!accepting}
                      disabled={!canConfirm}
                      onClick={onConfirm}
                    >
                      {accepting ? 'Accept offers' : 'Decline offers'}
                    </StyledButton>
                  </>
                )}
              </StyledButtonContainer>
            </>
          )}
        </StyledPopUpContainer>
      </StyledPopUpBlock>
    </StyledPopUp>
  );
}
